import React, { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faXmark, faSquarePlus, faMobileScreen } from "@fortawesome/free-solid-svg-icons";
import { usePwaInstall } from "../../hooks/usePwaInstall";
import IosInstallSteps from "./IosInstallSteps";

const DISMISS_KEY = "n450s:a2hs-dismissed-at";
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // don't nag again for 2 weeks
const SHOW_DELAY_MS = 2500; // let the page settle before sliding in

const slideUp = keyframes`
  from { transform: translateY(140%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const Wrapper = styled.div`
  position: fixed;
  left: 12px;
  right: 12px;
  /* float above the fixed mobile nav bar (bottom:0, ~72px tall) */
  bottom: calc(84px + env(safe-area-inset-bottom, 0px));
  z-index: 1001;
  max-width: 460px;
  margin: 0 auto;
  background: #fff7d6;
  border: 2px solid rgb(35, 35, 35);
  border-radius: 4px;
  box-shadow: 4px 5px rgba(0, 0, 0, 0.7), 0 0 16px 2px rgba(255, 196, 0, 0.55);
  padding: 14px 14px 12px;
  animation: ${slideUp} 0.42s cubic-bezier(0.25, 0.8, 0.25, 1);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #444;
  width: 30px;
  height: 30px;
  font-size: 1rem;
  border-radius: 4px;

  &:hover {
    color: #000;
    background: rgba(0, 0, 0, 0.07);
  }
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 1.05rem;
  color: #1a1a1a;
  padding-right: 26px;

  svg {
    color: #f29d00;
  }
`;

const Body = styled.p`
  margin: 6px 0 12px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #3a3a2e;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid rgb(35, 35, 35);
  box-shadow: 3px 3px rgba(0, 0, 0, 0.6);
  background: #60fac1;
  color: rgb(20, 20, 20);
  font-weight: 700;
  font-size: 0.95rem;
  padding: 10px 16px;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);

  &:hover:not(:disabled) {
    background: #36e7a6;
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const LaterButton = styled.button`
  border: none;
  background: transparent;
  color: #6a6a5a;
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 6px 4px;

  &:hover {
    color: #1a1a1a;
  }
`;

const StepsBox = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed rgba(0, 0, 0, 0.25);
`;

/**
 * A gentle, dismissible nudge to add NΛSOS to the home screen — shown only on
 * mobile, only when the site is running in a normal browser tab (not already
 * installed), and only when an install path actually exists.
 */
const InstallBanner: React.FC = () => {
  const { isStandalone, isMobile, installAvailable, installMethod, promptInstall } = usePwaInstall();
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(true); // assume hidden until storage is read
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      const at = raw ? parseInt(raw, 10) : 0;
      setDismissed(at > 0 && Date.now() - at < COOLDOWN_MS);
    } catch {
      setDismissed(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const remember = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  const onAdd = useCallback(async () => {
    if (installMethod === "ios") {
      setShowIosSteps((s) => !s);
      return;
    }
    setWorking(true);
    const outcome = await promptInstall();
    setWorking(false);
    // Accepted → appinstalled hides us anyway. Dismissed → respect the cooldown.
    if (outcome !== "unavailable") remember();
  }, [installMethod, promptInstall, remember]);

  if (!ready || dismissed || !isMobile || isStandalone || !installAvailable) {
    return null;
  }

  return (
    <Wrapper role="dialog" aria-label="Add NΛSOS to your home screen">
      <CloseButton type="button" aria-label="Dismiss" onClick={remember}>
        <FontAwesomeIcon icon={faXmark} />
      </CloseButton>

      <Heading>
        <FontAwesomeIcon icon={faBolt} />
        Make it feel like an app
      </Heading>
      <Body>
        Add NΛSOS to your home screen for a better shot at getting out of the day 😏 — full-screen,
        faster, no browser bar. Honestly, it&apos;s basically already an app.
      </Body>

      <ActionRow>
        <AddButton type="button" onClick={onAdd} disabled={working}>
          <FontAwesomeIcon icon={installMethod === "ios" ? faSquarePlus : faMobileScreen} />
          {installMethod === "ios"
            ? showIosSteps
              ? "Hide steps"
              : "Show me how"
            : working
              ? "Opening…"
              : "Add to home screen"}
        </AddButton>
        <LaterButton type="button" onClick={remember}>
          Maybe later
        </LaterButton>
      </ActionRow>

      {installMethod === "ios" && showIosSteps && (
        <StepsBox>
          <IosInstallSteps />
        </StepsBox>
      )}
    </Wrapper>
  );
};

export default InstallBanner;
